import {
  useHttpClient,
  useNetworkStatus,
  useToast,
} from '@airship-platform/airship-patterns';
import React, {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from 'react';
import { PriceDisplayMode } from './priceDisplayMode.models';
import {
  initState,
  priceDisplayModeReducer,
  PriceDisplayModeState,
} from './priceDisplayMode.reducer';

interface PriceDisplayModeContextProps {
  priceDisplayMode: PriceDisplayMode[];
  currentPriceDisplayMode: PriceDisplayMode | null;
  isLoading: boolean;
  isLoaded: boolean;
  fetchPriceDisplayModes: () => Promise<void>;
}

const PriceDisplayModeContext = createContext<
  PriceDisplayModeContextProps | undefined
>(undefined);

type OwnProps = PropsWithChildren<{
  initialState?: Partial<PriceDisplayModeState>;
}>;

export const PriceDisplayModeProvider: React.FC<OwnProps> = ({
  children,
  initialState,
}) => {
  const [state, dispatch] = useReducer(priceDisplayModeReducer, {
    ...initState,
    ...initialState,
  });
  const isOnline = useNetworkStatus();
  const httpClient = useHttpClient();
  const { showToast } = useToast();

  useEffect(() => {
    if (state.error) {
      showToast(state.error, 'error');
    }
  }, [state.error, showToast]);

  const priceDisplayMode = useMemo(
    () => state.ids.map((id) => state.entities[id]),
    [state.entities, state.ids]
  );

  const currentPriceDisplayMode = useMemo(
    () => state.entities[state.currentAccountingId || ''],
    [state.currentAccountingId, state.entities]
  );

  const fetchPriceDisplayModes: PriceDisplayModeContextProps['fetchPriceDisplayModes'] =
    useCallback(async () => {
      if (!isOnline()) {
        dispatch({
          type: 'SET_LOADING_STATE',
          payload: { isLoading: false, isLoaded: true },
        });
        return;
      }
      dispatch({ type: 'FETCH_PRICE_DISPLAY_MODES' });
      try {
        const priceDisplayModes = await httpClient.get<PriceDisplayMode[]>(
          `/organization-accounting/price-display-mode`
        );
        dispatch({
          type: 'FETCH_PRICE_DISPLAY_MODES_SUCCESS',
          payload: { priceDisplayModes },
        });
      } catch (error) {
        dispatch({
          type: 'FETCH_PRICE_DISPLAY_MODES_FAILURE',
          payload: { error: `Failed to fetch priceDisplayModes: ${error}` },
        });
      }
    }, [httpClient, isOnline]);

  return (
    <PriceDisplayModeContext.Provider
      value={{
        priceDisplayMode,
        currentPriceDisplayMode,
        isLoading: state.isLoading,
        isLoaded: state.isLoaded,
        fetchPriceDisplayModes,
      }}
    >
      {children}
    </PriceDisplayModeContext.Provider>
  );
};

export const usePriceDisplayMode = (): PriceDisplayModeContextProps => {
  const context = useContext(PriceDisplayModeContext);
  if (context === undefined) {
    throw new Error(
      'usePriceDisplayMode must be used within a PriceDisplayModeProvider'
    );
  }
  return context;
};
